/**
 * Sentry SDK init for the puku-pcb web app.
 *
 * Why this lives in its own module:
 *   - The DSN is read from `import.meta.env.VITE_SENTRY_DSN` at build time
 *     (Vite exposes `VITE_*` to the client bundle). If the DSN is unset —
 *     typical for local dev — `initSentry()` is a no-op so contributors
 *     without a project don't get a Sentry error in their console on
 *     boot.
 *   - The matching Sentry project is `4512208349691904` (browser side of
 *     the same Sentry org + region the API worker uses). The release id
 *     is the short git SHA so deploys line up with the API worker's
 *     `SENTRY_RELEASE` env var.
 *
 * Mirrors the API worker's sample-rate shape:
 *   - dev: 1.0 (see everything)
 *   - production: 0.1 (the typical Sentry default; raise it if signal
 *     looks thin).
 *
 * `tracePropagationTargets` is the URL allow-list for distributed
 * tracing headers. We propagate to the API origin and to the sandbox-fs
 * origin (the two backends the UI calls).
 *
 * Called once, as the very first import in `main.tsx`, before React
 * mounts — earlier than the existing `ErrorBoundary` mount because
 * `Sentry.init()` registers global error handlers we'd otherwise miss
 * during the initial render.
 */
import * as Sentry from "@sentry/react";

interface SentryEnv {
  /** Sentry DSN; injected by Vite from `VITE_SENTRY_DSN`. */
  dsn?: string;
  /** "production" | "staging" | "development" | undefined. */
  mode?: string;
  /** Short git SHA; injected at build time from `CF_PAGES_COMMIT_SHA`. */
  release?: string;
}

export function initSentry(env: SentryEnv = {}): void {
  if (!env.dsn) {
    // No-op when no DSN is configured (typical for local dev). The React
    // tree still mounts; nothing else changes.
    return;
  }

  const isProd = env.mode === "production";
  const tracesSampleRate = isProd ? 0.1 : 1.0;
  const replaysSessionSampleRate = isProd ? 0.0 : 0.1;
  const replaysOnErrorSampleRate = 1.0;

  Sentry.init({
    dsn: env.dsn,
    release: env.release,
    environment: env.mode ?? "development",
    integrations: [
      // BrowserTracing picks up navigation + fetch spans automatically.
      // We don't add the React Router v6/v7 instrumentation here because
      // Sentry 8 ships it built-in via `browserTracingIntegration()`
      // when `reactRouterV6BrowserTracingIntegration` isn't installed —
      // which keeps the dep surface smaller.
      Sentry.browserTracingIntegration({
        // URL allow-list for distributed-tracing header propagation.
        // The browser SDK only adds `sentry-trace` + `baggage` to fetch /
        // XHR requests matching one of these patterns. Anything else
        // (e.g. third-party CDNs, analytics) is left alone.
        //
        // Matches:
        //   - `localhost` (Vite dev: web :5173 → API :8788 / sandbox :8787)
        //   - the puku-pcb API + sandbox-fs hosts in dev (explicit ports)
        //   - the production workers under `*.workers.dev` and any other
        //     same-origin fetches.
        tracePropagationTargets: [
          "localhost",
          /^https?:\/\/localhost:(5173|8788|8787)\//,
          /^https:\/\/[^/]*\.workers\.dev\//,
          // Same-origin fetches (relative URLs, e.g. `/api/...`).
          /^\/(api|sandboxes)\//,
        ],
      }),
      Sentry.replayIntegration({
        // Mask everything by default. Text bodies and inputs are scrubbed
        // unless we want to debug a specific UX flow.
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate,
    replaysSessionSampleRate,
    replaysOnErrorSampleRate,
    // Don't send PII (user ids, IPs) by default. The UI is anonymous
    // until the user signs in; revisit once auth lands.
    sendDefaultPii: false,
  });
}

/**
 * Read Sentry env from Vite's `import.meta.env` + a build-time release
 * id. Kept separate so tests can stub it.
 */
export function readSentryEnv(): SentryEnv {
  return {
    dsn: import.meta.env.VITE_SENTRY_DSN,
    mode: import.meta.env.MODE,
    release:
      import.meta.env.VITE_SENTRY_RELEASE ??
      // Wrangler Pages injects CF_PAGES_COMMIT_SHA at build time. Local
      // `vite` dev doesn't, so this is undefined there — that's fine,
      // Sentry will bucket events under the default release.
      // @ts-expect-error — Vite env type defs don't include CF bindings.
      import.meta.env.CF_PAGES_COMMIT_SHA,
  };
}